/**
 * Which PDF template a recommendation should render with.
 *
 * Corn seed recommendations get the v2 layout; everything else — soy, weeds, crop protection
 * and corn density — keeps the original. Derived from the recommendation rather than passed in
 * by each caller: PDFs are generated from at least six places (the dashboard list, the
 * my-recommendations table, field monitoring, the legacy editor, the read-only page and the v2
 * result page), and any that forgot to pass it silently produced the wrong template.
 *
 * Returns `undefined` when the original template applies, so callers can spread the result and
 * let the backend's own default stand.
 *
 * Deliberately dependency-free: it's imported from hooks and dashboard components, and pulling
 * in the router here would drag the whole view registry along with it.
 */
type RecommendationLike = {
    crop?: string;
    crops?: { crop?: string }[];
    weeds?: unknown[];
    cornDensity?: unknown;
    weedV2?: unknown;
    diseaseRecommendation?: unknown;
    plagueRecommendation?: unknown;
    seedTreatmentRecommendation?: unknown;
};

export const seedPdfVersion = (recommendation?: RecommendationLike | null): number | undefined => {
    if (!recommendation) return undefined;

    // These are separate recommendation types that happen to share the PDF endpoint.
    const isCropProtection = !!(
        recommendation.weedV2 ||
        recommendation.diseaseRecommendation ||
        recommendation.plagueRecommendation ||
        recommendation.seedTreatmentRecommendation
    );
    if (isCropProtection || recommendation.cornDensity || recommendation.weeds?.length) return undefined;

    const crop = recommendation.crop || recommendation.crops?.[0]?.crop;
    return crop === 'corn' ? 2 : undefined;
};
